import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import { Translate } from 'react-localize-redux';
import { trackMixpanelEvent } from 'utils/general';
import { useFetchCurrenciesTimezones } from 'signup/hooks';
import { movePopularCurrenciesUpFront, POPULAR_CURRENCIES } from '../signup/utils';
import css from '../signup/style.css';
import agencyCss from './style.css';
import TextField from '../components/molecules/TextField';
import Button from '../components/atoms/Button';
import Tooltip from '../components/molecules/Tooltip';
import Icon, { IconSizes, IconTypes } from '../components/foundations/Icon';
import WarningMessage from '../components/utilities/WarningMessage';
import ThankYou from '../signup/components/thankYou';
import ShowPasswordIcon from '../resources/svg/visible.svg';
import HidePasswordIcon from '../resources/svg/hide.svg';
import { RECAPTCHA_SIGNUP_SITE_KEY } from '../signup/consts';
import {
    validateFullName,
    validateEmail,
    validatePassword,
    validateAgencyName,
    isValidAgencyForm,
    AGENCY_ERROR_CODES,
    ACTIVE_CLIENTS_OPTIONS,
} from './utils';
import {
    MIXPANEL_PREFIX,
    TRANSLATE_PREFIX,
    SIGNUP_TRANSLATE_PREFIX,
    SIGNUP_STATUS_SUCCESS,
} from './consts';
import { WIZARD_SESSION_KEY, PARTNER_PATH } from '../accountTypeWizard/consts';
import AgencySidebar from './AgencySidebar';
import FormDropdown from './FormDropdown';

function getServerErrorMessage(errorCode) {
    const messages = {
        [AGENCY_ERROR_CODES.ERROR_EMAIL_IN_BLACK_LIST]: `${TRANSLATE_PREFIX}.ERROR_EMAIL_IN_BLACK_LIST`,
        [AGENCY_ERROR_CODES.ERROR_REJECTED_EMAIL]: `${TRANSLATE_PREFIX}.ERROR_REJECTED_EMAIL`,
        [AGENCY_ERROR_CODES.ERROR_RECAPTCHA_VALIDATION_FAILED]: `${TRANSLATE_PREFIX}.ERROR_RECAPTCHA_VALIDATION_FAILED`,
    };
    return messages[errorCode] || errorCode || '';
}

function AgencySignup({
    onSubmitClick,
    onResendClick,
    onCheckAgencyName,
    status,
    errorCode,
    isLoading,
    emailSent,
    nameAvailable,
}) {
    const [formData, setFormData] = useState({
        companyName: '',
        fullName: '',
        email: '',
        activeAdvertiserClients: '',
        timezoneName: '',
        currencyCode: '',
        password: '',
        passwordConfirmation: '',
        passwordTextFieldType: 'password',
        confirmPasswordTextFieldType: 'password',
        recaptchaToken: null,
    });
    const [validations, setValidations] = useState({});
    const pageLoadTime = useRef(null);

    const {
        value: {
            currencies: allCurrencies,
            timezones: allTimeZones,
        } = {
            currencies: [],
            timezones: [],
        },
    } = useFetchCurrenciesTimezones();

    const currencies = useMemo(() => {
        if (allCurrencies.length > 0) {
            movePopularCurrenciesUpFront(allCurrencies, POPULAR_CURRENCIES);
            return allCurrencies.map(c => ({ value: c.name, label: c.display_name }));
        }
        return [];
    }, [allCurrencies]);

    const timeZones = useMemo(() => {
        if (allTimeZones.length > 0) {
            return allTimeZones.map(tz => ({ value: tz, label: tz }));
        }
        return [];
    }, [allTimeZones]);

    useEffect(() => {
        pageLoadTime.current = new Date();
        trackMixpanelEvent(MIXPANEL_PREFIX, 'Page Load');
    }, []);

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        const clearableFields = ['companyName', 'fullName', 'email', 'password', 'passwordConfirmation'];
        if (clearableFields.includes(field)) {
            setValidations(v => ({ ...v, [field]: null }));
        }
    };

    const onAgencyNameChange = value => {
        updateField('companyName', value);
        onCheckAgencyName(value, formData.email);
    };

    const onReCaptchaChange = token => {
        updateField('recaptchaToken', token);
        setValidations(v => ({ ...v, recaptcha: null }));
    };

    const togglePasswordType = () => {
        updateField('passwordTextFieldType', formData.passwordTextFieldType === 'password' ? 'text' : 'password');
    };

    const toggleConfirmPasswordType = () => {
        updateField('confirmPasswordTextFieldType', formData.confirmPasswordTextFieldType === 'password' ? 'text' : 'password');
    };

    const onSignupClick = () => {
        const wizardAnswers = JSON.parse(sessionStorage.getItem(WIZARD_SESSION_KEY) || 'null');
        if (wizardAnswers) sessionStorage.removeItem(WIZARD_SESSION_KEY);

        const newValidations = {
            companyName: validateAgencyName(formData.companyName),
            fullName: validateFullName(formData.fullName),
            email: validateEmail(formData.email),
            password: validatePassword(formData.password),
            passwordConfirmation: formData.password !== formData.passwordConfirmation
                ? `${TRANSLATE_PREFIX}.PASSWORDS_DO_NOT_MATCH`
                : null,
            activeAdvertiserClients: formData.activeAdvertiserClients ? null : `${TRANSLATE_PREFIX}.FIELD_REQUIRED`,
            timezoneName: formData.timezoneName ? null : `${TRANSLATE_PREFIX}.FIELD_REQUIRED`,
            currencyCode: formData.currencyCode ? null : `${TRANSLATE_PREFIX}.FIELD_REQUIRED`,
        };

        setValidations(newValidations);

        if (isValidAgencyForm(newValidations) && formData.recaptchaToken) {
            trackMixpanelEvent(MIXPANEL_PREFIX, 'Submit', {
                signup_email: formData.email,
                company_name: formData.companyName,
                time_to_submit: (new Date() - pageLoadTime.current) / 1000,
                success: 'true',
                ...(wizardAnswers || {}),
            });
            onSubmitClick({
                fullName: formData.fullName,
                email: formData.email,
                password: formData.password,
                companyName: formData.companyName,
                activeAdvertiserClients: formData.activeAdvertiserClients,
                timezoneName: formData.timezoneName,
                currencyCode: formData.currencyCode,
                recaptchaToken: formData.recaptchaToken,
            });
        } else {
            if (!formData.recaptchaToken) {
                setValidations(v => ({ ...v, recaptcha: `${SIGNUP_TRANSLATE_PREFIX}.INVALID_FIELD_RECAPTCHA_TOKEN` }));
            }
            trackMixpanelEvent(MIXPANEL_PREFIX, 'Submit', {
                signup_email: formData.email,
                company_name: formData.companyName,
                time_to_submit: (new Date() - pageLoadTime.current) / 1000,
                success: 'false',
                ...(wizardAnswers || {}),
            });
        }
    };

    if (status === SIGNUP_STATUS_SUCCESS) {
        return (
            <div className={css.signupContainer}>
                <ThankYou
                    onResendClick={onResendClick}
                    isLoading={isLoading}
                    emailSent={emailSent}
                    email={formData.email}
                />
            </div>
        );
    }

    return (
        <div className={css.signupContainer}>
            <AgencySidebar />
            <div className={classNames(css.signupForm, agencyCss.agencySignupForm)}>
                <div className={classNames(css.signupFormInner, agencyCss.agencySignupFormInner)}>
                    <div className={agencyCss.agencyFormTopSection}>
                        <div className={agencyCss.agencyFormTitle}>
                            <Translate id={`${TRANSLATE_PREFIX}.FORM_TITLE`} />
                        </div>
                        <div className={agencyCss.agencyFormSubTitle}>
                            <Translate id={`${TRANSLATE_PREFIX}.FORM_SUBTITLE`} />
                        </div>
                        <div className={css.singularFormBody}>
                            <WarningMessage
                                show={!!errorCode}
                                showIcon={false}
                                showTypeIcon
                                type="error"
                                message={getServerErrorMessage(errorCode)}
                            />

                            {/* Agency name with availability indicator */}
                            <TextField
                                inputConfig={{ maxLength: 100 }}
                                error={validations.companyName}
                                value={formData.companyName}
                                containerClass={css.textField}
                                label={`${TRANSLATE_PREFIX}.AGENCY_NAME_LABEL`}
                                onChange={onAgencyNameChange}
                                disabled={isLoading}
                            />
                            {formData.companyName && nameAvailable === false && (
                                <div className={classNames(agencyCss.nameAvailabilityMessage, agencyCss.nameUnavailable)}>
                                    <Translate id={`${TRANSLATE_PREFIX}.NAME_UNAVAILABLE`} />
                                </div>
                            )}

                            <TextField
                                inputConfig={{ maxLength: 61 }}
                                error={validations.fullName}
                                value={formData.fullName}
                                containerClass={css.textField}
                                label={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_NAME_FIELD_LABEL`}
                                onChange={val => updateField('fullName', val)}
                                disabled={isLoading}
                            />
                            <TextField
                                inputConfig={{ maxLength: 70 }}
                                error={validations.email}
                                value={formData.email}
                                containerClass={css.textField}
                                label={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_EMAIL_FIELD_LABEL`}
                                onChange={val => updateField('email', val)}
                                disabled={isLoading}
                            />

                            <FormDropdown
                                labelKey={`${TRANSLATE_PREFIX}.ACTIVE_CLIENTS_LABEL`}
                                placeholderKey={`${TRANSLATE_PREFIX}.SELECT_RANGE_PLACEHOLDER`}
                                value={formData.activeAdvertiserClients}
                                onChange={val => { updateField('activeAdvertiserClients', val); setValidations(v => ({ ...v, activeAdvertiserClients: null })); }}
                                error={validations.activeAdvertiserClients}
                                isLoading={isLoading}
                                options={ACTIVE_CLIENTS_OPTIONS}
                            />
                            <FormDropdown
                                labelKey={`${TRANSLATE_PREFIX}.TIMEZONE_LABEL`}
                                placeholderKey={`${TRANSLATE_PREFIX}.SELECT_TIMEZONE_PLACEHOLDER`}
                                value={formData.timezoneName}
                                onChange={val => { updateField('timezoneName', val); setValidations(v => ({ ...v, timezoneName: null })); }}
                                error={validations.timezoneName}
                                isLoading={isLoading}
                                options={timeZones}
                            />
                            <FormDropdown
                                labelKey={`${TRANSLATE_PREFIX}.CURRENCY_LABEL`}
                                placeholderKey={`${TRANSLATE_PREFIX}.SELECT_CURRENCY_PLACEHOLDER`}
                                value={formData.currencyCode}
                                onChange={val => { updateField('currencyCode', val); setValidations(v => ({ ...v, currencyCode: null })); }}
                                error={validations.currencyCode}
                                isLoading={isLoading}
                                options={currencies}
                            />
                            {/* Password */}
                            <div className={css.passwordField}>
                                <Tooltip
                                    interactive
                                    wrapperClass={classNames(css.tooltip, css.passwordTooltip)}
                                    titleTranslationKey={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_PASSWORD_TOOLTIP`}
                                >
                                    <Icon type={IconTypes.MINGCUTE} name="question-line" size={IconSizes.MEDIUM} />
                                </Tooltip>
                                {formData.password && formData.passwordTextFieldType === 'password' && (
                                    <Tooltip
                                        interactive
                                        titleTranslationKey={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_SHOW_PASSWORD_TOOLTIP`}
                                        style={{ position: 'absolute', right: 0 }}
                                        position="left"
                                    >
                                        <ShowPasswordIcon className={css.showPasswordIcon} onClick={togglePasswordType} />
                                    </Tooltip>
                                )}
                                {formData.password && formData.passwordTextFieldType === 'text' && (
                                    <HidePasswordIcon className={css.hidePasswordIcon} onClick={togglePasswordType} />
                                )}
                                <Tooltip
                                    interactive
                                    titleTranslationKey={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_PASSWORD_TOOLTIP`}
                                    trigger="click"
                                    position="top-start"
                                >
                                    <TextField
                                        inputConfig={{ maxLength: 50 }}
                                        error={validations.password}
                                        value={formData.password}
                                        containerClass={css.textField}
                                        label={`${SIGNUP_TRANSLATE_PREFIX}.SIGNUP_FORM_PASSWORD_FIELD_LABEL`}
                                        onChange={val => updateField('password', val)}
                                        type={formData.passwordTextFieldType}
                                        disabled={isLoading}
                                    />
                                </Tooltip>
                            </div>
                            <div className={css.passwordField}>
                                {formData.passwordConfirmation && formData.confirmPasswordTextFieldType === 'password' && (
                                    <ShowPasswordIcon className={css.showPasswordIcon} onClick={toggleConfirmPasswordType} />
                                )}
                                {formData.passwordConfirmation && formData.confirmPasswordTextFieldType === 'text' && (
                                    <HidePasswordIcon className={css.hidePasswordIcon} onClick={toggleConfirmPasswordType} />
                                )}
                                <TextField
                                    inputConfig={{ maxLength: 50 }}
                                    error={validations.passwordConfirmation}
                                    value={formData.passwordConfirmation}
                                    containerClass={css.textField}
                                    label={`${TRANSLATE_PREFIX}.CONFIRM_PASSWORD_LABEL`}
                                    onChange={val => updateField('passwordConfirmation', val)}
                                    type={formData.confirmPasswordTextFieldType}
                                    disabled={isLoading}
                                />
                            </div>

                            <div className={css.recaptcha}>
                                <ReCAPTCHA sitekey={RECAPTCHA_SIGNUP_SITE_KEY} onChange={onReCaptchaChange} />
                            </div>
                            {validations.recaptcha && (
                                <div className={css.errorMessage}>{validations.recaptcha}</div>
                            )}

                            <div className={css.termsText}>
                                <span><Translate id={`${TRANSLATE_PREFIX}.TERMS_TEXT`} /></span>
                                <a href="https://www.singular.net/terms/" target="_blank" rel="noopener noreferrer">
                                    <Translate id={`${TRANSLATE_PREFIX}.TERMS_LINK_TEXT`} />
                                </a>.
                            </div>

                            <div className={css.submit}>
                                <Button
                                    onClick={onSignupClick}
                                    type="primary"
                                    level="level1"
                                    text={`${TRANSLATE_PREFIX}.SUBMIT_BUTTON`}
                                    showSpinner={isLoading}
                                    className={css.submitButton}
                                    textClassName={css.submitButtonText}
                                />
                            </div>
                            <div className={css.footerSeparator} />
                            <div className={css.loginLink}>
                                <span><Translate id={`${TRANSLATE_PREFIX}.LOGIN_LINK`} /></span>
                                <a href="/login"><Translate id={`${TRANSLATE_PREFIX}.LOGIN_LINK_TEXT`} /></a>
                            </div>
                            <div className={agencyCss.partnerLink}>
                                <span><Translate id={`${TRANSLATE_PREFIX}.PARTNER_LINK`} /></span>
                                <a href={PARTNER_PATH}><Translate id={`${TRANSLATE_PREFIX}.PARTNER_LINK_TEXT`} /></a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

AgencySignup.propTypes = {
    onSubmitClick: PropTypes.func.isRequired,
    onResendClick: PropTypes.func.isRequired,
    onCheckAgencyName: PropTypes.func.isRequired,
    status: PropTypes.string,
    errorCode: PropTypes.string,
    isLoading: PropTypes.bool.isRequired,
    emailSent: PropTypes.bool.isRequired,
    nameAvailable: PropTypes.bool,
};

export default AgencySignup;
